import { Check, ChevronDown, SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { getJobRequirement } from "@endpoints/hiringCompany/requirements";
import JobRequirementCard from "./JobRequirementCard";
import RequirementContextForm from "./RequirementContextForm";
import FieldError from "./FieldError";
import { JobFormValues } from "../schema/jobForm.schema";
import { fromRequirement } from "../lib/jobPayload";
import { useHiringCompanies } from "../hooks/useHiringCompanies";

const ImportRequirementForm = () => {
  const {
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<JobFormValues>();
  const [companyId, companyName, importedRequirementId] = useWatch({
    control,
    name: ["companyId", "companyName", "jobRequirementId"],
  });

  const [searchText, setSearchText] = useState(companyName ?? "");
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const hiringCompanies = useHiringCompanies(searchText);

  const [jobRequirements, setJobRequirements] = useState([]);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [isHiringCompanyDropdownOpened, setIsHiringCompanyDropdownOpened] =
    useState(false);

  const importedRequirement = jobRequirements?.find(
    (requirement) => requirement?._id === importedRequirementId,
  );

  // Only show requirements shared by the selected hiring company.
  const filteredRequirements = companyName
    ? jobRequirements?.filter(
        (requirement) =>
          (companyId &&
            (requirement?.companyId === companyId ||
              requirement?.company?._id === companyId)) ||
          requirement?.company?.name?.trim()?.toLowerCase() ===
            companyName.trim().toLowerCase(),
      )
    : jobRequirements;

  const selectHiringCompany = (company) => {
    setValue("companyId", company?._id ?? "", { shouldDirty: true });
    setValue("companyName", company?.companyName ?? "");
    setSearchText(company?.companyName ?? "");
    setShowSearchSuggestions(false);
  };

  useEffect(() => {
    let isCancelled = false;
    getJobRequirement()
      .then(({ data, success }) => {
        if (success && !isCancelled) setJobRequirements(data ?? []);
      })
      .catch(() => {});
    return () => {
      isCancelled = true;
    };
  }, []);

  const importRequirement = (requirement) => {
    Object.entries(fromRequirement(requirement)).forEach(([name, value]) => {
      setValue(name as keyof JobFormValues, value as never, {
        shouldDirty: true,
      });
    });
    clearErrors(["jobRequirementId", "companyId"]);
  };

  return (
    <>
      {/* Partnered requirement library */}
      <div className="rounded-md border">
        <div className="flex items-center justify-between border-b px-4 py-3 bg-slate-100">
          <div className="flex flex-col">
            <span className="font-bold text-xss text-light-black uppercase tracking-widest">
              Partnered requirement library
            </span>
            <span className="text-md font-semibold text-black">
              Select an approved requirement
            </span>
          </div>
          {/* Search Hiring company */}
          <div className="flex flex-col">
            <span className="uppercase text-xsss font-bold text-light-black">
              Hiring company{hiringCompanies?.length}
            </span>
            <div className="relative flex items-center gap-1 px-2 rounded-md h-9 bg-white border w-56">
              <SearchIcon size={14} color="lightgray" />
              <input
                className="outline-none text-xss font-medium flex-1"
                placeholder="Search"
                value={searchText}
                onFocus={() => setShowSearchSuggestions(true)}
                onBlur={() => setShowSearchSuggestions(false)}
                onChange={(e) => {
                  // Clearing the search removes the company filter.
                  if (!e?.target?.value) selectHiringCompany(null);
                  setSearchText(e?.target?.value);
                  setShowSearchSuggestions(true);
                }}
              />
              {showSearchSuggestions && hiringCompanies?.length ? (
                <div className="flex flex-col absolute top-10 left-0 bg-white overflow-hidden rounded-md border w-full z-10 p-1">
                  {hiringCompanies?.map((item) => {
                    const isSelected = item?.hiringCompany?._id === companyId;
                    return (
                      <button
                        key={item?.hiringCompany?._id}
                        type="button"
                        className="flex items-center justify-between hover:cursor-pointer hover:bg-slate-100 gap-1 w-full p-2 rounded-md"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectHiringCompany(item?.hiringCompany)}
                      >
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-md bg-black text-white font-bold text-xs flex items-center justify-center uppercase">
                            {item?.hiringCompany?.companyName?.charAt(0)}
                          </div>
                          <div className="flex flex-col text-left">
                            <span className="text-xss font-semibold leading-3 capitalize">
                              {item?.hiringCompany?.companyName}
                            </span>
                            <span className="text-xsss font-medium text-light-black leading-4">
                              {item?.totalJobs > 0
                                ? `${item?.totalJobs} approved requirement`
                                : "No approved requirements"}
                            </span>
                          </div>
                        </div>
                        {isSelected ? <Check size={7} color="black" /> : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              {/* <DropdownMenu
                open={isHiringCompanyDropdownOpened}
                onOpenChange={setIsHiringCompanyDropdownOpened}
              >
                <DropdownMenuTrigger className="border-l pl-1 outline-none">
                  <ChevronDown className="w-4 h-4" color="gray" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white" align="end">
                  {hiringCompanies?.map((item) => {
                    const isSelected = item?.hiringCompany?._id === companyId;

                    return (
                      <DropdownMenuItem
                        key={item?.hiringCompany?._id}
                        className="flex items-center justify-between hover:cursor-pointer hover:bg-slate-50 gap-1 w-64"
                        onSelect={() => {
                          setValue("companyId", item?.hiringCompany?._id, {
                            shouldDirty: true,
                          });
                          setValue(
                            "companyName",
                            item?.hiringCompany?.companyName,
                          );
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-md bg-black text-white font-bold text-xs flex items-center justify-center uppercase">
                            {item?.hiringCompany?.companyName?.charAt(0)}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-xss font-semibold leading-3 capitalize">
                              {item?.hiringCompany?.companyName}
                            </span>
                            <span className="text-xsss font-medium text-light-black leading-4">
                              {item?.totalJobs > 0
                                ? `${item?.totalJobs} approved requirement`
                                : "No approved requirements"}
                            </span>
                          </div>
                        </div>
                        {isSelected ? <Check size={7} color="black" /> : null}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu> */}
            </div>
          </div>
        </div>

        {filteredRequirements?.length ? (
          <div className="flex flex-col">
            {filteredRequirements?.map((data) => {
              return (
                <JobRequirementCard
                  key={data?._id}
                  data={data}
                  importedRequirementDetails={importedRequirement}
                  setImportedRequirementDetails={importRequirement}
                  selectedRequirement={selectedRequirement}
                  setSelectedRequirement={setSelectedRequirement}
                />
              );
            })}
          </div>
        ) : (
          // Empty placeholder
          <div className="bg-[#fbfcfc] border-t p-5">
            <div className="flex flex-col items-center justify-center h-28 bg-white border border-dashed border-light-grey rounded-md">
              <span className="font-semibold text-xs text-black">
                No approved requirements
              </span>
              <span className="font-medium text-xss text-light-black">
                {companyName || "This partnered hiring company"} has not shared
                an approved requirement with your agency.
              </span>
            </div>
          </div>
        )}

        {errors.jobRequirementId?.message ? (
          <div className="border-t px-4 py-2.5">
            <FieldError message={errors.jobRequirementId.message} />
          </div>
        ) : null}
      </div>

      {/* Requirement Context Form */}
      <RequirementContextForm isImported />

      {/* Source Note */}
      {importedRequirement?.additionalNotes ? (
        <div className="flex gap-5 border-l-[3px] border-slate-600 bg-slate-100 px-4 py-3.5 mt-4">
          <span className="uppercase font-bold text-xss text-slate-600 shrink-0">
            Source note
          </span>
          <span className="text-xss font-medium text-light-black">
            {importedRequirement.additionalNotes} This comment is private to
            your agency and will not be published.
          </span>
        </div>
      ) : null}
    </>
  );
};

export default ImportRequirementForm;
